import { Injectable, inject, WritableSignal, signal } from '@angular/core';
import { Router } from '@angular/router';
import { environment } from '../../../../environments/environment';
import { BehaviorSubject, empty, EMPTY, Observable, of } from 'rxjs';
import * as auth0 from 'auth0-js';
import { HttpClient } from '@angular/common/http';
import { SelectableClient } from '../../models/selectable-client';
import { Auth0Client } from '@auth0/auth0-spa-js';
import { TokenInfo, UserInfo, UserInfoView } from '../../models';
import createAuth0Client from '@auth0/auth0-spa-js';
import { v4 as uuidv4 } from 'uuid';
import { LocaleService } from './locale.service';
import * as Sentry from '@sentry/angular';
import { SettingsService } from './settings.service';
import { DefaultsService } from './defaults.service';
import { Heap } from '../../interface/heap';
import { LocalStorageService } from './local-storage.service';

@Injectable({
  providedIn: 'root',
})
export class AuthenticationService {
  public auth0PkceClient: Auth0Client;
  public auth0ImplicitClient: any = null;
  public Authenticated = new BehaviorSubject(false);
  public userInfoSubject = new BehaviorSubject<UserInfo | null>(null);
  public isauthenticationhandled: boolean = false;
  public isauthenticated: boolean = false;
  public tokenInfo: TokenInfo;
  public userInfo: WritableSignal<UserInfo | null> = signal(null);
  public profile = new BehaviorSubject<any>(null);
  public userInfoView: UserInfoView;
  public selectedClientIdSubject = new BehaviorSubject<number>(0);
  public selectedClientId: WritableSignal<number | null> = signal(null);

  public getSelectableClients(searchTerm?: string): Array<SelectableClient> {
    let result: SelectableClient[] = [];
    if (this.userInfoView && this.userInfoView.SelectableClients) {
      if (searchTerm) {
        return this.userInfoView.SelectableClients.filter(
          (r) =>
            r.ClientShortName.toLocaleLowerCase().indexOf(
              searchTerm.toLocaleLowerCase()
            ) !== -1
        );
      } else {
        result = this.userInfoView.SelectableClients;
      }
    } else {
      result.push(new SelectableClient());
    }

    this.userInfoSubject.next(this.userInfoView);

    return result;
  }

  constructor(
    public router: Router,
    public httpclient: HttpClient,
    public localeService: LocaleService,
    public settingsService: SettingsService,
    private localStorageService: LocalStorageService
  ) {
    try {
      const client_id = this.getParameter('cid');
      if (client_id) {
        this.settingsService.load(Number.parseInt(client_id, 10));
      }
    } catch (ex: any) {
      Sentry.captureException(ex);
    }

    if (!environment.isPkceFlowMode) {
      this.auth0ImplicitClient = new auth0.WebAuth({
        clientID: environment.clientId,
        domain: environment.domain,
        responseType: environment.responseType,
        redirectUri: this.getCallBackUrl(),
        scope: environment.scope,
        audience: environment.audience,
      });
    }
    this.selectedClientIdSubject.subscribe((clientId) => {
      if (clientId) {
        this.selectedClientId.set(clientId);
        const client = this.getSelectableClients().find(
          (r) => Number(r.ClientID) === Number(clientId)
        );
        if (!client?.MQARestricted) {
          this.router.navigate(['categories']);
        } else {
          this.router.navigate(['restricted']);
        }
      }
    });
  }

  public getParameter(paramName: string) {
    const searchParams = new URLSearchParams(window.location.search);
    return searchParams.get(paramName);
  }

  public resetIdentity = () => {
    ((window as any).heap as Heap).resetIdentity();
  };

  public async getAuth0PkceClient(): Promise<Auth0Client> {
    if (!this.auth0PkceClient) {
      const config = {
        client_id: environment.clientId,
        domain: environment.domain,
        responseType: environment.responseType,
        redirectUri: this.getCallBackUrl(),
        scope: environment.scope,
        audience: environment.audience,
      };
      this.auth0PkceClient = await createAuth0Client(config);
      const isAuthenticated = await this.auth0PkceClient.isAuthenticated();
      this.Authenticated.next(isAuthenticated); // Provide the current value of isAuthenticated
      this.Authenticated.subscribe(async (isAuthenticated) => {
        // Whenever isAuthenticated changes, provide the current value of `getUser`
        if (isAuthenticated) {
          // ***** authentication has been detected in pkce mode *****
          // get the basic information need to support the application (the access token and related information, the user information and accessible clients)
          this.isauthenticated = true;
          this.profile.next(await this.auth0PkceClient.getUser());
        } else {
          this.profile.next(null);
        }
      });
    }
    return this.auth0PkceClient;
  }

  public async login() {
    const supportedLocale = this.localeService.getSupportedLocale(
      this.localeService.getLocale()
    );
    if (environment.isPkceFlowMode === true) {
      this.auth0PkceClient = await this.getAuth0PkceClient();
      this.Authenticated.subscribe((value) => {
        this.isauthenticated = value;
      });
      this.profile.subscribe((profile) => {
        this.profile = profile;
      });
      await this.auth0PkceClient.loginWithRedirect({
        redirect_uri: this.getCallBackUrl(),
        ui_locales: supportedLocale,
      });
    } else {
      console.log('using auth0 supported locale: ' + supportedLocale);
      this.auth0ImplicitClient.authorize({
        ui_locales: supportedLocale,
      });
    }
  }

  public handleAuthentication(defaultsService: DefaultsService): void {
    this.auth0ImplicitClient.parseHash(
      (
        err: any,
        authResult: { accessToken: any; idToken: any; IdToken: any }
      ) => {
        this.isauthenticationhandled = true;
        const isproceed = true;
        if (isproceed) {
          if (authResult && authResult.accessToken && authResult.idToken) {
            // ***** authentication has been detected in implicit mode *****
            this.tokenInfo = new TokenInfo(
              authResult.IdToken,
              authResult.accessToken
            );
            try {
              const url = `${environment.apiUrl}/mobile-metrics/user-info`;
              this.httpclient.get(url).subscribe(
                (userInfo: any) => {
                  if (userInfo) {
                    this.localStorageService.setClientId(userInfo.ClientId);
                    this.userInfo.set(userInfo);
                    const userName = userInfo.userName;
                    Sentry.setUser({
                      username: userName,
                    });
                    if (this.enablePerformanceMonitoring()) {
                      const client = Sentry.getClient();
                      if (client) {
                        client.getOptions().enabled = false;
                        client.getOptions().tracesSampleRate = 1.0;
                        client.getOptions().enabled = true;
                      }
                    }
                    let selectedclient: SelectableClient;
                    this.userInfoView = new UserInfoView(
                      userInfo,
                      this.tokenInfo
                    ); // this will most of the user information except the selected client
                    if (this.userInfoView.Lang) {
                      this.localeService.setLocale(this.userInfoView.Lang);
                    }

                    if (this.userInfoView.SelectableClients.length > 0) {
                      const selectedClientIdFromQuery = Number.parseInt(
                        this.getParameter('cid') ?? '',
                        10
                      );
                      const previouslySelectedClientId =
                        this.settingsService.getStoredValue(
                          this.userInfoView.clientSettingsKey
                        );
                      const preferredClientId = Number.isNaN(
                        selectedClientIdFromQuery
                      )
                        ? previouslySelectedClientId
                        : selectedClientIdFromQuery;

                      const selectedClientId =
                        this.userInfoView.SelectableClients.findIndex(
                          (r) =>
                            Number(r.ClientID) === Number(preferredClientId)
                        );

                      selectedclient =
                        this.userInfoView.SelectableClients[
                          selectedClientId === -1 ? 0 : selectedClientId
                        ];
                      if (selectedclient && selectedclient.MQARestricted) {
                        this.router.navigate(['/restricted']);
                      }
                      this.userInfoView.updateSelectedClient(
                        selectedclient,
                        defaultsService
                      );
                      this.selectedClientIdSubject.next(
                        selectedclient.ClientID
                      );
                    } else {
                      // must be 0 length, user has no access to any client
                      this.blockUnauthorizedUser();
                    }
                    console.log(
                      'authentication.service userInfoSubject should update.  '
                    );
                    this.userInfoSubject.next(userInfo);
                    // this.userInfoSubject.complete();
                    this.setAuthenticated(true);
                  }
                },
                (error) => {
                  if (error.status === '401') {
                    // window.location.href = 'unauthorized';  // this is a cleaner presentation than router.navigate
                    this.blockUnauthorizedUser();
                    Sentry.captureException(error);
                  }
                }
              );
            } catch (ex: any) {
              Sentry.captureException(ex);
            }
          } else if (err) {
          }
        }
      }
    );
  }

  public startSentryTransaction = (name: string) => {
    const transaction = Sentry.startTransaction({
      name: name,
      tags: {
        userId: this.getUserId(),
        clientId: this.getClientId(),
      },
    });
    if (transaction) {
      Sentry.getCurrentHub().configureScope((scope) =>
        scope.setSpan(transaction)
      );
      const span = transaction.startChild({
        op: name,
        data: {},
      });
      Sentry.getCurrentHub().configureScope((scope) => scope.setSpan(span));
    }
  };

  public enablePerformanceMonitoring = () => {
    return (
      environment.platform === 'Production' &&
      this.userInfoView &&
      this.userInfoView.UserName &&
      environment.ukTestingNames.includes(
        this.userInfoView.UserName.toLocaleLowerCase()
      )
    );
  };

  public finishSentryTransaction = () => {
    try {
      const span = Sentry.getCurrentHub().getScope().getSpan();
      if (span) {
        span.finish();
      }
      const transaction = Sentry.getCurrentHub().getScope().getTransaction();
      if (transaction) {
        transaction.finish();
        Sentry.getCurrentHub().configureScope((scope) =>
          scope.setSpan(undefined)
        );
      }
    } catch (error) {
      Sentry.captureException(error);
    }
  };

  public setAuthenticated(value: boolean): void {
    this.isauthenticated = value;
    this.Authenticated.next(value);
  }

  // public promptClientSelection = (): Observable<SelectableClient> => {
  //     const result = new BehaviorSubject<SelectableClient>(new SelectableClient());
  //     const dialogConfig = new MatDialogConfig();
  //     let dialogData = new ClientSelectionDialogData(
  //         this._userInfoView.SelectableClients,
  //         this._userInfoView.SelectedClient ? this._userInfoView.SelectedClient.ClientID : null);
  //     dialogConfig.disableClose = true;
  //     dialogConfig.autoFocus = true;
  //     dialogConfig.data = dialogData;
  //     this.materialdialog.open(ClientSelectorComponent, dialogConfig).afterClosed().subscribe((dialogresponse: { selectedClient: SelectableClient | null; } | null) => {
  //         if (dialogresponse != null && dialogresponse.selectedClient != null) {
  //             result.next(dialogresponse.selectedClient);
  //         } else {
  //             //
  //         }
  //         result.complete();
  //     });
  //     return result.asObservable();
  // }

  public blockUnauthorizedUser = () => {
    // const dialogConfig = new MatDialogConfig();
    // let dialogData = null;
    // dialogConfig.disableClose = false; // true;
    // dialogConfig.autoFocus = true;
    // dialogConfig.data = dialogData;
    this.router.navigate(['signout']);
    // this.materialdialog.open(UnauthorizedComponent, dialogConfig).afterClosed().subscribe(() => {
    //     this.router.navigate(['signout']);
    // });
  };

  public getAuthToken(): Observable<TokenInfo> {
    if (
      this.userInfoView &&
      this.userInfoView.tokenInfo &&
      this.userInfoView.tokenInfo.ValidTo > new Date()
    ) {
      return of(this.userInfoView.tokenInfo);
    } else {
      if (this.tokenInfo && this.tokenInfo.ValidTo > new Date()) {
        return of(this.tokenInfo);
      }
    }
    return EMPTY;
  }

  public getSentryTraceId(): Observable<string> {
    const result: string = uuidv4();

    return of(result);
  }

  public renewTokens(): void {
    if (environment.isPkceFlowMode === true) {
      //
    } else {
      this.auth0ImplicitClient.checkSession(
        {},
        (err: any, authResult: { accessToken: any; idToken: any }) => {
          if (authResult && authResult.accessToken && authResult.idToken) {
            //
          } else if (err) {
            this.logout();
          }
        }
      );
    }
  }

  public logout(): void {
    if (
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      /* @ts-ignore */
      window.MSInputMethodContext &&
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      /* @ts-ignore */
      !!window.MSInputMethodContext &&
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      /* @ts-ignore */
      !!document.documentMode
    ) {
      document.execCommand('ClearAuthenticationCache');
    }
    if (environment.isPkceFlowMode === true) {
      this.auth0PkceClient.logout({ returnTo: window.location.origin });
    } else {
      this.auth0ImplicitClient.logout({ returnTo: window.location.origin });
    }
  }

  public get baseApiUrl(): Observable<string> {
    const apiurl = environment.apiUrl;
    return of(apiurl);
  }

  public getCallBackUrl = (): string => {
    let result: string = '';
    const prefix: string = window.location.origin;
    result = prefix + '/callback';
    return result;
  };

  public getUserId = () => {
    if (this.userInfoView) {
      return this.userInfoView.UserId;
    }
    return null;
  };

  public getClientId = () => {
    if (this.userInfoView && this.userInfoView.SelectedClient) {
      return this.userInfoView.SelectedClient.ClientID;
    }
    return null;
  };
}
